import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './contexts/ThemeContext';
import { UIPreferencesProvider } from './contexts/UIPreferencesContext';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { FeatureFlagProvider } from './providers/FeatureFlagProvider';
import AppLayout from './components/layout/AppLayout';
import EntitlementGuard from './components/ui/EntitlementGuard';
import ErrorBoundary from './components/ErrorBoundary';
import FeatureFlagsUnavailable from './components/FeatureFlagsUnavailable';
import FlaggedRoute from './components/FlaggedRoute';
import { useFlag, useFeatureFlags } from './hooks/useFeatureFlags';
import AccountNotProvisioned from './pages/AccountNotProvisioned';
import AmsUnavailable from './pages/AmsUnavailable';
import ConsentGate from './components/ConsentGate';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import {
  ACCEPT_INVITE_PATH,
  isAuthOptionalPath,
  isPublicPolicyPath,
  PRIVACY_PATH,
} from './utils/publicPolicyPaths';
import RunnerPage from './pages/RunnerPage';
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import ServicesPage from './pages/ServicesPage';
import ServiceComposePage from './pages/ServiceComposePage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import AgentsPage from './pages/AgentsPage';
import AgentDetailPage from './pages/AgentDetailPage';
import NewAgentPage from './pages/NewAgentPage';
import RedesignedAgentDetailPage from './pages/agents/AgentDetailPage';
import RedesignedNewAgentPage from './pages/agents/NewAgentPage';
import ConfigsPage from './pages/ConfigsPage';
import ConfigDetailPage from './pages/ConfigDetailPage';
import NewConfigPage from './pages/NewConfigPage';
import CollaborationsPage from './pages/CollaborationsPage';
// Run Explorer (task 1151): the run page for every entry point.
import RunExplorerPage from './pages/RunExplorerPage';
import EndpointsPage from './pages/EndpointsPage';
import EndpointDetailPage from './pages/EndpointDetailPage';
import EndpointRunDetailPage from './pages/EndpointRunDetailPage';
import TestComparisonPage from './pages/TestComparisonPage';
import NewEndpointPage from './pages/NewEndpointPage';
import PromptsPage from './pages/PromptsPage';
import PromptDetailPage from './pages/PromptDetailPage';
import NewPromptPage from './pages/NewPromptPage';
import ModelsPage from './pages/ModelsPage';
import ModelDetailPage from './pages/ModelDetailPage';
import {
  ModelConfigDetailRedirect,
  ModelConfigsListRedirect,
  NewModelConfigRedirect,
} from './components/models/ModelConfigRedirects';
import LegacyModelConfigsRedirect from './components/LegacyModelConfigsRedirect';
import McpConnectionsPage from './pages/McpConnectionsPage';
import McpConnectionDetailPage from './pages/McpConnectionDetailPage';
import NewMcpConnectionPage from './pages/NewMcpConnectionPage';
import KnowledgeDocumentsPage from './pages/KnowledgeDocumentsPage';
import KnowledgeDocumentPage from './pages/KnowledgeDocumentPage';
import KnowledgeBasesPage from './pages/KnowledgeBasesPage';
import ArtifactsPage from './pages/ArtifactsPage';
import NewKnowledgeBasePage from './pages/NewKnowledgeBasePage';
import KnowledgeBaseDetailPage from './pages/KnowledgeBaseDetailPage';
import MessageRoutesPage from './pages/MessageRoutesPage';
import MessageRouteDetailPage from './pages/MessageRouteDetailPage';
import NewMessageRoutePage from './pages/NewMessageRoutePage';
import SettingsBillingRoute from './components/SettingsBillingRoute';
import EmailPrivacyPage from './pages/EmailPrivacyPage';
import ProfilePage from './pages/ProfilePage';
import PreferencesPage from './pages/PreferencesPage';
import SecurityPage from './pages/SecurityPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentPage from './pages/PaymentPage';
import AccountGeneralPage from './pages/AccountGeneralPage';
import TeamPage from './pages/TeamPage';
import ApiKeysPage from './pages/ApiKeysPage';
import AccountModelsPage from './pages/AccountModelsPage';
import SettingsSpendingPage from './pages/SettingsSpendingPage';
import SettingsLayout from './components/layout/SettingsLayout';
import ArchivePage from './pages/ArchivePage';
import SupportPage from './pages/SupportPage';
import AcceptInvitePage from './pages/AcceptInvitePage';

const queryClient = new QueryClient();

/**
 * AuthGate blocks rendering the app until Keycloak init is complete.
 * While loading, shows a full-screen spinner. On init_timeout or
 * redirect_failed, shows a minimal error recovery UI.
 * Must be rendered inside AuthProvider.
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const {
    isLoading,
    authError,
    login,
    clearAuthError,
    isAuthenticated,
    isAccountProvisioned,
    isAmsUnavailable,
  } = useAuthContext();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-3 text-gray-500 dark:text-gray-400">
          <div className="h-8 w-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
          <span className="text-sm">Signing in...</span>
        </div>
      </div>
    );
  }

  if (authError === 'redirect_failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center p-6">
          <p className="text-gray-700 dark:text-gray-300 text-sm">
            Authentication failed. This can happen when the login redirect returns with an error.
          </p>
          <button
            onClick={() => { clearAuthError(); login(); }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors"
          >
            Sign in again
          </button>
        </div>
      </div>
    );
  }

  if (authError === 'init_timeout') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center p-6">
          <p className="text-gray-700 dark:text-gray-300 text-sm">
            Connection to the identity provider timed out.
          </p>
          <button
            onClick={() => { clearAuthError(); login(); }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  // Auth-optional route (task 731): the invitation landing page must render for
  // a visitor who has no login yet — that is the whole fix, since bouncing them
  // to Keycloak is what produced the contextless sign-in form kv captured.
  // AuthProvider already skips its login redirect for this path; this branch
  // renders the page on its own, outside AppLayout and the account/consent
  // gates that an anonymous visitor cannot possibly satisfy. It stays behind
  // `innovate.fe.invite-accept.page` exactly as before, and the moment the same
  // person returns signed in, the normal stack below takes over.
  if (!isAuthenticated && isAuthOptionalPath(window.location.pathname)) {
    return (
      <Router>
        <Routes>
          <Route
            path={ACCEPT_INVITE_PATH}
            element={
              <FlaggedRoute flag="innovate.fe.invite-accept.page" element={<AcceptInvitePage />} />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    );
  }

  // Authenticated with Keycloak, but AMS was unreachable at token-mint time —
  // a transient outage. Distinct from "no account". Checked first so a
  // temporary outage is never mistaken for a missing account.
  if (isAuthenticated && isAmsUnavailable) {
    return <AmsUnavailable />;
  }

  // Authenticated with Keycloak, but no provisioned AMS account (no account_id,
  // ams_status=not_provisioned, or a 403 MISSING_ACCOUNT_SCOPE fallback fired).
  if (isAuthenticated && !isAccountProvisioned) {
    return <AccountNotProvisioned />;
  }

  // Consent gate (task 422): blocks all app routes while the token carries
  // consent_pending=true. `/privacy` and `/terms` are NOT affected — they
  // render on the public branch in App() before this stack ever mounts.
  return <ConsentGate>{children}</ConsentGate>;
}

/**
 * Runner retirement (task 114). The standalone Runner page is absorbed by the
 * endpoint hub's Test tab (`/endpoints/:id?tab=test`).
 *
 * Rollout is staged via the `innovate.fe.runner.page` flag (seeded ON in
 * Unleash): while the flag is ON the old RunnerPage still renders for a clean
 * transition; once it is turned OFF the redirects take over —
 *   - `/runner?endpointId=X` → `/endpoints/X?tab=test`
 *   - bare `/runner`         → `/endpoints`
 * RunnerPage.tsx is left in place; its deletion is a follow-up cleanup after
 * the flag is retired in Unleash.
 */
function RunnerRoute() {
  const { ready } = useFeatureFlags();
  const runnerPageEnabled = useFlag('innovate.fe.runner.page');
  const [searchParams] = useSearchParams();

  // Avoid redirecting (or flashing the page) before flags have loaded.
  if (!ready) {
    return null;
  }

  if (runnerPageEnabled) {
    return <RunnerPage />;
  }

  const endpointId = searchParams.get('endpointId');
  if (endpointId) {
    return <Navigate to={`/endpoints/${endpointId}?tab=test`} replace />;
  }
  return <Navigate to="/endpoints" replace />;
}

/**
 * Adaptive landing behavior at `/` (tasks 115, 377).
 *
 * Task 377 makes the Dashboard the post-auth landing page: when
 * `innovate.fe.dashboard.page` is enabled, `/` redirects to `/dashboard` so
 * `/dashboard` stays the canonical URL. The redirect target is gated by the
 * SAME flag as the `/dashboard` route itself, so we never redirect to a route
 * whose own flag is off (the task-339 loop guard holds by construction).
 *
 * With the dashboard flag OFF, the pre-377 chain is preserved, gated behind
 * `innovate.fe.home.page`:
 *   - flag ON  → render the adaptive HomePage (invitation / apps / operational)
 *   - flag OFF → preserve the prior behavior (task 114) of redirecting to
 *                `/endpoints` — but only when `innovate.fe.endpoints.page` is
 *                itself enabled. Redirecting while the endpoints flag is off
 *                would let `/` and `/endpoints` point at each other and loop
 *                the router until the browser throttles the History API
 *                (preprod incident 2026-08-05, task 339). With no safe
 *                redirect target, render a stable unavailable panel instead.
 * The `*` catch-all also points at `/`, so unknown routes resolve to
 * Dashboard-or-Home-or-redirect depending on the flags.
 *
 * Exported for component tests.
 */
export function HomeRoute() {
  const { ready } = useFeatureFlags();
  const dashboardPageEnabled = useFlag('innovate.fe.dashboard.page');
  const homePageEnabled = useFlag('innovate.fe.home.page');
  const endpointsPageEnabled = useFlag('innovate.fe.endpoints.page');

  // Avoid redirecting (or flashing the page) before flags have loaded.
  if (!ready) {
    return null;
  }

  // Dashboard is the canonical landing page; `/dashboard` is gated on this
  // exact flag, so this redirect can never target a flag-off route.
  if (dashboardPageEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  if (homePageEnabled) {
    return <HomePage />;
  }

  if (endpointsPageEnabled) {
    return <Navigate to="/endpoints" replace />;
  }

  return <FeatureFlagsUnavailable />;
}

/**
 * Composer retirement from the nav (task 377). The graph-first ComposerPage
 * leaves the top nav and its `/composer` route becomes a redirect into the
 * AI Services section — since task 392, straight to the compose-based
 * creation entry at `/services/new`. Honoring the task-339 guard, the
 * redirect only targets `/services/new` while the flag that route reads,
 * `innovate.fe.services.new.page` (task 1216), is enabled; otherwise it falls
 * back to `/`, which always resolves to a
 * stable render (HomeRoute never navigates back here). ComposerPage.tsx was
 * deleted with the standalone Composer client (task 1117).
 *
 * The standalone UI Composer page (`/compose`) was retired in task 1132;
 * interfaces are created from the AI Service's Interface tab via the Assistant.
 *
 * Exported for component tests.
 */
export function ComposerRedirect() {
  const { ready } = useFeatureFlags();
  const newServicePageEnabled = useFlag('innovate.fe.services.new.page');

  // Avoid redirecting before flags have loaded.
  if (!ready) {
    return null;
  }

  if (newServicePageEnabled) {
    return <Navigate to="/services/new" replace />;
  }

  return <Navigate to="/" replace />;
}

/**
 * `/agents/:id` and `/agents/new` (task 1312): the redesigned pages while
 * `innovate.fe.agents.redesign` is on, the previous pages unchanged while it
 * is off — the rollback lever. The previous pages and their private components
 * stay in the tree as this flag-off branch until the flag has been on in
 * production for a release cycle; a follow-up task deletes them.
 *
 * Exported for component tests.
 */
export function AgentDetailRoute() {
  const { ready } = useFeatureFlags();
  const redesign = useFlag('innovate.fe.agents.redesign');
  // Avoid flashing one page and then the other before flags have loaded.
  if (!ready) return null;
  return redesign ? <RedesignedAgentDetailPage /> : <AgentDetailPage />;
}

/** See `AgentDetailRoute`. Exported for component tests. */
export function NewAgentRoute() {
  const { ready } = useFeatureFlags();
  const redesign = useFlag('innovate.fe.agents.redesign');
  if (!ready) return null;
  return redesign ? <RedesignedNewAgentPage /> : <NewAgentPage />;
}

function App() {
  // Public policy pages (task 422): `/privacy` and `/terms` are linked from
  // the Keycloak registration page (task 421) and from the ConsentGate, so
  // they must render WITHOUT the auth stack — AuthProvider force-redirects
  // unauthenticated visitors to Keycloak, and ConsentGate blocks pending
  // users. This branch runs on initial load only (no Router below it); the
  // gate and registration page link here with full page loads (new tab).
  if (isPublicPolicyPath(window.location.pathname)) {
    const page =
      window.location.pathname.replace(/\/$/, '') === PRIVACY_PATH ? (
        <PrivacyPolicyPage />
      ) : (
        <TermsOfUsePage />
      );
    return (
      <UIPreferencesProvider>
        <ThemeProvider>{page}</ThemeProvider>
      </UIPreferencesProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <UIPreferencesProvider>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <FeatureFlagProvider>
              <AuthGate>
              <ErrorBoundary>
              <Router>
                <AppLayout>
                  <EntitlementGuard>
                  <Routes>
                    <Route path="/" element={<HomeRoute />} />
                    <Route path="/dashboard" element={<FlaggedRoute flag="innovate.fe.dashboard.page" element={<DashboardPage />} />} />
                    <Route path="/services" element={<FlaggedRoute flag="innovate.fe.services.page" element={<ServicesPage />} />} />
                    {/* Registered before /services/:id so "new" is never matched as an id (task 392). */}
                    <Route path="/services/new" element={<FlaggedRoute flag="innovate.fe.services.new.page" element={<ServiceComposePage />} />} />
                    <Route path="/services/:id" element={<FlaggedRoute flag="innovate.fe.services.page" element={<ServiceDetailPage />} />} />
                    {/* Run detail reuses EndpointRunDetailPage (same :id/:collaborationId
                        params); its internal back links still point at
                        /endpoints/:id?tab=runs — see task 377 activity log. */}
                    <Route path="/services/:id/runs/:collaborationId" element={<FlaggedRoute flag="innovate.fe.services.run-detail.page" element={<EndpointRunDetailPage />} />} />
                    {/* The version comparison report (task 1178): two test runs as ?a=&b=, linkable.
                        Its own page flag is the routing lever (task 1216); the test runs feature
                        lever (task 1215) gates it too, since comparing is a test runs action. */}
                    <Route path="/services/:id/testing/compare" element={<FlaggedRoute flag="innovate.fe.testing.compare.page" element={<FlaggedRoute flag="innovate.fe.testing.runs" element={<TestComparisonPage />} />} />} />
                    <Route path="/runner" element={<RunnerRoute />} />
                    <Route path="/composer" element={<ComposerRedirect />} />
                    <Route path="/agents" element={<FlaggedRoute flag="innovate.fe.agents.page" element={<AgentsPage />} />} />
                    {/* Each /new route also reads its entity's .create flag (task 1216), so a
                        typed URL honors the flag that hides the list page's New button. */}
                    <Route path="/agents/new" element={<FlaggedRoute flag="innovate.fe.agents.page" element={<FlaggedRoute flag="innovate.fe.agents.create" element={<NewAgentRoute />} />} />} />
                    <Route path="/agents/:id" element={<FlaggedRoute flag="innovate.fe.agents.page" element={<AgentDetailRoute />} />} />
                    <Route path="/configs" element={<FlaggedRoute flag="innovate.fe.configs.page" element={<ConfigsPage />} />} />
                    <Route path="/configs/new" element={<FlaggedRoute flag="innovate.fe.configs.page" element={<FlaggedRoute flag="innovate.fe.configs.create" element={<NewConfigPage />} />} />} />
                    <Route path="/configs/:id" element={<FlaggedRoute flag="innovate.fe.configs.page" element={<ConfigDetailPage />} />} />
                    <Route path="/collaborations" element={<FlaggedRoute flag="innovate.fe.collaborations.page" element={<CollaborationsPage />} />} />
                    {/* The Run Explorer has its own page flag (task 1217); the legacy list above keeps collaborations.page. */}
                    <Route path="/collaborations/:id" element={<FlaggedRoute flag="innovate.fe.run-explorer.page" element={<RunExplorerPage />} />} />
                    {/* Run Explorer (task 1151): the address "View run" links to, with an optional ?turn=N. */}
                    <Route path="/collaborations/:collaborationId/runs/:runId" element={<FlaggedRoute flag="innovate.fe.run-explorer.page" element={<RunExplorerPage />} />} />
                    <Route path="/endpoints" element={<FlaggedRoute flag="innovate.fe.endpoints.page" element={<EndpointsPage />} />} />
                    <Route path="/endpoints/new" element={<FlaggedRoute flag="innovate.fe.endpoints.page" element={<FlaggedRoute flag="innovate.fe.endpoints.create" element={<NewEndpointPage />} />} />} />
                    <Route path="/endpoints/:id" element={<FlaggedRoute flag="innovate.fe.endpoints.page" element={<EndpointDetailPage />} />} />
                    <Route path="/endpoints/:id/runs/:collaborationId" element={<FlaggedRoute flag="innovate.fe.endpoints.page" element={<EndpointRunDetailPage />} />} />
                    <Route path="/prompts" element={<FlaggedRoute flag="innovate.fe.prompts.page" element={<PromptsPage />} />} />
                    <Route path="/prompts/new" element={<FlaggedRoute flag="innovate.fe.prompts.page" element={<FlaggedRoute flag="innovate.fe.prompts.create" element={<NewPromptPage />} />} />} />
                    <Route path="/prompts/:id" element={<FlaggedRoute flag="innovate.fe.prompts.page" element={<PromptDetailPage />} />} />
                    {/* Model catalog (task 1074). Model Configs were LLM Configs;
                        the read-only Model Catalog lists the Models they run
                        on. One flag gates both (the renamed
                        innovate.fe.llm-configs.page). Old /llm-configs URLs
                        redirect, keeping the rest of the path. */}
                    <Route path="/model-catalog" element={<FlaggedRoute flag="innovate.fe.model-catalog.page" element={<ModelsPage />} />} />
                    <Route path="/model-catalog/review" element={<FlaggedRoute flag="innovate.fe.models.review" element={<ModelsPage />} />} />
                    <Route path="/model-catalog/models/:id" element={<FlaggedRoute flag="innovate.fe.model-catalog.page" element={<ModelDetailPage />} />} />
                    {/* The Model Config pages retired into the Models page (task 1167);
                        their URLs redirect there with the Model expanded. */}
                    <Route path="/model-configs" element={<FlaggedRoute flag="innovate.fe.model-catalog.page" element={<ModelConfigsListRedirect />} />} />
                    <Route path="/model-configs/new" element={<FlaggedRoute flag="innovate.fe.model-catalog.page" element={<NewModelConfigRedirect />} />} />
                    <Route path="/model-configs/:id" element={<FlaggedRoute flag="innovate.fe.model-catalog.page" element={<ModelConfigDetailRedirect />} />} />
                    <Route path="/llm-configs/*" element={<LegacyModelConfigsRedirect />} />
                    <Route path="/mcp-connections" element={<FlaggedRoute flag="innovate.fe.mcp-connections.page" element={<McpConnectionsPage />} />} />
                    <Route path="/mcp-connections/new" element={<FlaggedRoute flag="innovate.fe.mcp-connections.page" element={<FlaggedRoute flag="innovate.fe.mcp-connections.create" element={<NewMcpConnectionPage />} />} />} />
                    <Route path="/mcp-connections/:id" element={<FlaggedRoute flag="innovate.fe.mcp-connections.page" element={<McpConnectionDetailPage />} />} />
                    {/* Knowledge (task 574). Documents is the primary surface —
                        upload and ingestion status live there; knowledge bases
                        are groupings that decide scope. "new" is registered
                        before ":id" so it is never matched as an id. */}
                    <Route path="/knowledge-documents" element={<FlaggedRoute flag="innovate.fe.knowledge.documents.page" element={<KnowledgeDocumentsPage />} />} />
                    {/* One document, rendered in the browser (task 1081). The
                        viewer body inside the page is behind
                        innovate.fe.knowledge.documents.view. */}
                    <Route path="/knowledge-documents/:id" element={<FlaggedRoute flag="innovate.fe.knowledge.documents.page" element={<KnowledgeDocumentPage />} />} />
                    <Route path="/knowledge-bases" element={<FlaggedRoute flag="innovate.fe.knowledge.bases.page" element={<KnowledgeBasesPage />} />} />
                    <Route path="/knowledge-bases/new" element={<FlaggedRoute flag="innovate.fe.knowledge.bases.page" element={<FlaggedRoute flag="innovate.fe.knowledge.bases.create" element={<NewKnowledgeBasePage />} />} />} />
                    <Route path="/knowledge-bases/:id" element={<FlaggedRoute flag="innovate.fe.knowledge.bases.page" element={<KnowledgeBaseDetailPage />} />} />
                    {/* Artifacts (task 828): the account-wide lens on what
                        collaborations produced. The per-AI-Service lens is the
                        same listing, mounted as a tab on the service detail
                        page — one endpoint, one component, two lenses. */}
                    <Route path="/artifacts" element={<FlaggedRoute flag="innovate.fe.artifacts.page" element={<ArtifactsPage />} />} />
                    <Route path="/message-routes" element={<FlaggedRoute flag="innovate.fe.message-routes.page" element={<MessageRoutesPage />} />} />
                    <Route path="/message-routes/new" element={<FlaggedRoute flag="innovate.fe.message-routes.page" element={<FlaggedRoute flag="innovate.fe.message-routes.create" element={<NewMessageRoutePage />} />} />} />
                    <Route path="/message-routes/:id" element={<FlaggedRoute flag="innovate.fe.message-routes.page" element={<MessageRouteDetailPage />} />} />
                    {/*
                      Settings shell (task 625). The `/settings` area is a
                      nested layout: SettingsLayout renders the grouped sidebar
                      (You / Account / Billing) and an <Outlet/>; each page
                      keeps its own `innovate.fe.*.page` flag guard. `/settings`
                      itself redirects to Profile. `/settings/billing` stays
                      valid — it is the Stripe return URL — and renders the new
                      itemized Billing page (task 630) via SettingsBillingRoute,
                      which falls back to the legacy BillingPage when the new
                      flag is rolled off.
                    */}
                    <Route path="/settings" element={<SettingsLayout />}>
                      <Route index element={<Navigate to="/settings/profile" replace />} />
                      <Route path="profile" element={<FlaggedRoute flag="innovate.fe.profile.page" element={<ProfilePage />} />} />
                      <Route path="email-privacy" element={<FlaggedRoute flag="innovate.fe.email-privacy.page" element={<EmailPrivacyPage />} />} />
                      <Route path="preferences" element={<FlaggedRoute flag="innovate.fe.settings-preferences.page" element={<PreferencesPage />} />} />
                      <Route path="security" element={<FlaggedRoute flag="innovate.fe.settings-security.page" element={<SecurityPage />} />} />
                      <Route path="account" element={<FlaggedRoute flag="innovate.fe.settings-account.page" element={<AccountGeneralPage />} />} />
                      <Route path="team" element={<FlaggedRoute flag="innovate.fe.settings-team.page" element={<TeamPage />} />} />
                      {/* The account's API keys — list, mint with a label, and
                          revoke (task 974). Account-scoped like Team, so it
                          sits in the same group. */}
                      <Route path="api-keys" element={<FlaggedRoute flag="innovate.fe.settings-api-keys.page" element={<ApiKeysPage />} />} />
                      {/* The account's model policy — its own provider keys, allowed
                          providers and Models, and defaults (task 1147). */}
                      <Route path="account/models" element={<FlaggedRoute flag="innovate.fe.settings-account-models.page" element={<AccountModelsPage />} />} />
                      {/* Where deleted resources live, and the only place they
                          come back from (task 865). Beside the account's own
                          settings because the ledger is account-scoped. */}
                      <Route path="archive" element={<FlaggedRoute flag="innovate.fe.archive.page" element={<ArchivePage />} />} />
                      <Route path="subscription" element={<FlaggedRoute flag="innovate.fe.settings-subscription.page" element={<SubscriptionPage />} />} />
                      <Route path="payment" element={<FlaggedRoute flag="innovate.fe.settings-payment.page" element={<PaymentPage />} />} />
                      <Route path="billing" element={<SettingsBillingRoute />} />
                      {/* The monthly spending limit, alert thresholds and hard
                          stop (task 1159). Owner-only cohort in v1. */}
                      <Route path="billing/spending" element={<FlaggedRoute flag="innovate.fe.spending.page" element={<SettingsSpendingPage />} />} />
                    </Route>
                    {/*
                      Contact support (task 607). Deliberately unflagged: the
                      Help menu's support entry and the help assistant's
                      unanswered-question affordance both navigate here, and a
                      flag that could hide the page would leave those pointing
                      at the catch-all redirect. Authentication is the only
                      gate — it inherits AuthGate like every route here.
                    */}
                    <Route path="/support" element={<SupportPage />} />
                    {/*
                      Account-invitation landing (task 635). Reached from the
                      emailed accept URL (`/accept-invite?token=…`). AuthGate has
                      already run, so the visitor is authenticated by the time
                      this renders; the page completes acceptance and shows a
                      clear error for a bad/expired/revoked/mismatched token.
                    */}
                    <Route path="/accept-invite" element={<FlaggedRoute flag="innovate.fe.invite-accept.page" element={<AcceptInvitePage />} />} />
                    {/*
                      Registration entry path (task 792). An ANONYMOUS visitor
                      never reaches this route — AuthProvider hands them to
                      Keycloak's registration form before anything renders. This
                      is the already-signed-in case: someone who clicks the same
                      marketing CTA while holding a session has nothing to
                      register, so they land where the CTA used to take them.
                    */}
                    <Route path="/register" element={<Navigate to="/" replace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </EntitlementGuard>
                </AppLayout>
              </Router>
              </ErrorBoundary>
              </AuthGate>
              </FeatureFlagProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </UIPreferencesProvider>
    </QueryClientProvider>
  );
}

export default App;
